import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { readSupportContext, SUPPORT_CONTEXT_EVENT } from '@/security/supportContext'
import { Field, actionLabel } from './VaultForms'
import { id, actions as supportedActions, type Data } from '@/services/legalControl/workflow'
import type { EvidenceChoice } from '@/services/vault/subjectVerifications'
import { businessEvidence } from '@/services/vault/businessEvidence'
import {
  businessChoices,
  businessSource,
  intakeHistory,
  intakeCommand,
  rememberIntake,
  storeIntakeBody,
  originalIntake,
  inspectIntake,
  submitIntake,
  cancelIntake,
  storedCancellation,
  type BusinessSource,
  type IntakeReference,
} from '@/services/vault/businessIntake'

export default function BusinessVerificationPanel() {
  const { user } = useAuth()
  const blocked = () => {
    try {
      return !!readSupportContext()
    } catch {
      return true
    }
  }
  const [support, setSupport] = useState(blocked)
  useEffect(() => {
    const refresh = () => setSupport(blocked())
    window.addEventListener(SUPPORT_CONTEXT_EVENT, refresh)
    return () => window.removeEventListener(SUPPORT_CONTEXT_EVENT, refresh)
  }, [])
  if (
    support ||
    !user ||
    user.authScope !== 'user' ||
    !['firm_owner', 'firm_admin', 'ca_partner'].includes(user.role) ||
    !user.permissions?.includes('txh.client.read') ||
    !user.currentSessionId
  )
    return null
  try {
    id(user.id)
    id(user.tenantId)
    id(user.currentSessionId)
  } catch {
    return null
  }
  return (
    <Panel
      key={JSON.stringify([
        user.id,
        user.tenantId,
        user.currentSessionId,
        user.role,
        [...new Set(user.permissions)].sort(),
      ])}
    />
  )
}
function Panel() {
  const { user } = useAuth()
  const firm = user!.tenantId,
    owner = user!.id,
    session = user!.currentSessionId!
  const [refs, setRefs] = useState<IntakeReference[]>([]),
    [broken, setBroken] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [receipt, setReceipt] = useState<Data | null>(null)
  const [choices, setChoices] = useState<Array<{ customer_id: number; business_name: string }>>([]),
    [total, setTotal] = useState(0),
    [page, setPage] = useState(1),
    [selected, setSelected] = useState(''),
    [source, setSource] = useState<BusinessSource | null>(null)
  const [docs, setDocs] = useState<EvidenceChoice[]>([]),
    [docTotal, setDocTotal] = useState(0),
    [docPage, setDocPage] = useState(1),
    [evidenceIds, setEvidenceIds] = useState<string[]>([])
  const [account, setAccount] = useState(''),
    [capacity, setCapacity] = useState(''),
    [role, setRole] = useState(''),
    [actions, setActions] = useState<string[]>([]),
    [ack, setAck] = useState(false),
    [active, setActive] = useState<IntakeReference | null>(null),
    [reason, setReason] = useState(''),
    [cancelAck, setCancelAck] = useState(false)
  const [evidenceMode, setEvidenceMode] = useState<'vault' | 'declaration'>('vault')
  const [declared, setDeclared] = useState<Array<{ evidence_id: string; digest: string }>>([])
  const [declaredId, setDeclaredId] = useState(''),
    [declaredHash, setDeclaredHash] = useState('')
  const flight = useRef(false),
    epoch = useRef(0)
  useEffect(() => {
    epoch.current++
    try {
      setRefs(intakeHistory(firm, owner))
    } catch {
      setBroken(true)
      setError('Original business history unavailable. Restore it before writing.')
    }
    return () => {
      epoch.current++
    }
  }, [firm, owner, session])
  async function perform(operation: () => Promise<Data | void>) {
    if (flight.current || broken) return
    try {
      if (readSupportContext()) {
        setError('Original firm identity required;exit support access.')
        return
      }
    } catch {
      setError('Original firm context unavailable.')
      return
    }
    flight.current = true
    const version = epoch.current
    setBusy(true)
    setError('')
    setReceipt(null)
    const live = () => version === epoch.current
    try {
      const result = await operation()
      if (live() && result) setReceipt(result)
    } catch (e) {
      if (live())
        setError(
          (e instanceof Error ? e.message : 'Business operation unavailable') +
            '. Keep original references;unavailable or not found does not prove no effect.'
        )
    } finally {
      if (live()) {
        setBusy(false)
        flight.current = false
      }
    }
  }
  function readBody(request: string) {
    const command = originalIntake(firm, owner, session, request)
    if (!command)
      throw new Error('Original declaration unavailable in this session;inspect status instead')
    return command
  }
  function finish(ref: IntakeReference, result: Data) {
    const terminal = result.state === 'cancelled' || result.state === 'no_effect'
    const updated = { ...ref, unknown: !!ref.cancel && !terminal }
    rememberIntake(firm, owner, updated)
    setActive(updated)
    setRefs(intakeHistory(firm, owner))
    return result
  }
  async function loadSources(next = page) {
    const version = epoch.current
    setSource(null)
    setSelected('')
    setDocs([])
    setEvidenceIds([])
    setDeclared([])
    setDeclaredId('')
    setDeclaredHash('')
    setDocPage(1)
    setDocTotal(0)
    setAck(false)
    setChoices([])
    await perform(async () => {
      const result = await businessChoices(next)
      if (version === epoch.current) {
        setChoices(result.items)
        setTotal(result.total)
        setPage(next)
      }
    })
  }
  async function loadEvidence(next = docPage) {
    const version = epoch.current
    setAck(false)
    setDocs([])
    setEvidenceIds([])
    setDeclared([])
    setDeclaredId('')
    setDeclaredHash('')
    await perform(async () => {
      if (!source) throw new Error('Inspect the selected business before selecting evidence')
      const result = await businessEvidence(source.client_id, next, 20)
      if (version === epoch.current) {
        setDocs(result.items)
        setDocTotal(result.total)
        setDocPage(next)
      }
    })
  }
  function submit() {
    void perform(async () => {
      if (!source || !ack || active || intakeHistory(firm, owner).some(r => r.unknown))
        throw new Error('Recover pending original requests first')
      const chosen = docs.filter(d => evidenceIds.includes(d.id))
      if (chosen.length !== evidenceIds.length || chosen.some(d => !d.available))
        throw new Error('Refresh current evidence')
      const statements =
        evidenceMode === 'vault'
          ? chosen.map(d => ({ evidence_id: d.id, digest: d.digest }))
          : declared
      const command = intakeCommand({
        request_id: crypto.randomUUID(),
        customer_id: source.customer_id,
        alias_id: source.alias_id,
        client_id: source.client_id,
        source_epoch: source.source_epoch,
        source_revision: source.source_revision,
        source_sha256: source.source_sha256,
        proposed_account_id: account,
        declared_capacity: capacity,
        requested_role: role,
        requested_actions: actions,
        declared_evidence: statements,
        acknowledge_unverified_request: true,
      })
      const ref = { request: id(command.request_id), unknown: true }
      storeIntakeBody(firm, owner, session, command)
      rememberIntake(firm, owner, ref)
      setActive(ref)
      setRefs(intakeHistory(firm, owner))
      const version = epoch.current,
        result = await submitIntake(command)
      if (version === epoch.current) return finish(ref, result)
    })
  }
  function recover(ref: IntakeReference) {
    void perform(async () => {
      setActive(ref)
      const version = epoch.current,
        result = await inspectIntake(ref.request)
      if (version === epoch.current) return finish(ref, result)
    })
  }
  function retry() {
    if (!active) return
    void perform(async () => {
      if (active.cancel) throw new Error('Recover the original cancellation instead')
      const command = readBody(active.request)
      rememberIntake(firm, owner, { ...active, unknown: true })
      setRefs(intakeHistory(firm, owner))
      const version = epoch.current,
        result = await submitIntake(command)
      if (version === epoch.current) return finish(active, result)
    })
  }
  function cancel() {
    if (!active) return
    void perform(async () => {
      if (!cancelAck) throw new Error('Confirm cancellation of the original unverified request')
      const command = readBody(active.request),
        cancel = active.cancel ?? crypto.randomUUID(),
        originalReason = storedCancellation(
          firm,
          owner,
          session,
          active.request,
          cancel,
          active.cancel ? undefined : reason
        ),
        ref = { ...active, cancel, unknown: true }
      rememberIntake(firm, owner, ref)
      setActive(ref)
      setRefs(intakeHistory(firm, owner))
      const version = epoch.current,
        result = await cancelIntake(command, cancel, originalReason)
      if (version === epoch.current) return finish(ref, result)
    })
  }
  const locked = busy || broken || !!active || refs.some(r => r.unknown)
  return (
    <section className="card p-5 space-y-4" aria-label="Business verification request">
      <h2 className="text-lg font-semibold">Propose a business controller or representative</h2>
      <p>
        A firm declaration is unverified and grants no access. Independent evidence review and
        account consent are required before any appointment.
      </p>
      <fieldset disabled={locked} className="space-y-3">
        <legend>Select an original business record</legend>
        <button className="btn" onClick={() => void loadSources()}>
          Load business customers
        </button>
        <label className="block">
          Business customer
          <select
            className="input"
            value={selected}
            onChange={e => {
              setSelected(e.target.value)
              setSource(null)
              setDocs([])
              setEvidenceIds([])
              setDeclared([])
              setDeclaredId('')
              setDeclaredHash('')
              setDocPage(1)
              setDocTotal(0)
              setAck(false)
            }}
          >
            <option value="">Choose a business</option>
            {choices.map(c => (
              <option key={c.customer_id} value={c.customer_id}>
                {c.business_name}
              </option>
            ))}
          </select>
        </label>
        <button
          className="btn"
          disabled={!selected}
          onClick={() => {
            const version = epoch.current
            setSource(null)
            setDocs([])
            setEvidenceIds([])
            setDeclared([])
            setDeclaredId('')
            setDeclaredHash('')
            setDocPage(1)
            setDocTotal(0)
            setAck(false)
            void perform(async () => {
              const result = await businessSource(Number(selected), firm)
              if (version === epoch.current) setSource(result)
            })
          }}
        >
          Inspect selected business
        </button>
        <div>
          <button className="btn" disabled={page <= 1} onClick={() => void loadSources(page - 1)}>
            Previous businesses
          </button>
          <button
            className="btn"
            disabled={page * 20 >= total}
            onClick={() => void loadSources(page + 1)}
          >
            Next businesses
          </button>
        </div>
        {source && (
          <p>
            Current business reference: {source.client_id}. This observation does not verify legal
            control.
          </p>
        )}
        <Field
          label="Proposed independent account reference"
          value={account}
          onChange={e => {
            setAccount(e.target.value)
            setAck(false)
          }}
        />
        <Field
          label="Declared business capacity"
          value={capacity}
          onChange={e => {
            setCapacity(e.target.value)
            setAck(false)
          }}
        />
        <label className="block">
          Business appointment role
          <select
            className="input"
            value={role}
            onChange={e => {
              setRole(e.target.value)
              setAck(false)
            }}
          >
            <option value="">Choose a role</option>
            <option value="legal_controller">Legal controller</option>
            <option value="representative">Representative</option>
          </select>
        </label>
        <fieldset>
          <legend>Requested actions</legend>
          {supportedActions.map(a => (
            <label className="block" key={a}>
              <input
                type="checkbox"
                checked={actions.includes(a)}
                onChange={e => {
                  setAck(false)
                  setActions(v => (e.target.checked ? [...v, a] : v.filter(x => x !== a)))
                }}
              />
              {actionLabel(a)}
            </label>
          ))}
        </fieldset>
        <label className="block">
          Evidence source
          <select
            className="input"
            value={evidenceMode}
            onChange={e => {
              setEvidenceMode(e.target.value as 'vault' | 'declaration')
              setAck(false)
              setDocs([])
              setEvidenceIds([])
              setDeclared([])
              setDeclaredId('')
              setDeclaredHash('')
            }}
          >
            <option value="vault">Authorized client Vault versions</option>
            <option value="declaration">Sealed evidence for independent review</option>
          </select>
        </label>
        {evidenceMode === 'vault' ? (
          <div className="space-y-3">
            <button
              className="btn"
              disabled={!source || !user!.permissions?.includes('txh.document.read')}
              onClick={() => void loadEvidence()}
            >
              Load declared evidence
            </button>
            <p>
              Evidence comes from immutable Vault versions you are currently authorized to read for
              this business. This proposal grants no access. Sealed declarations support initial
              claims without a pre-existing Vault read grant.
            </p>
            {source && docs.length === 0 && (
              <p>
                No evidence selected. Load authorized versions;an empty or denied result does not
                permit using the legacy firm document list.
              </p>
            )}
            {!user!.permissions?.includes('txh.document.read') && (
              <p>Document read permission is required to select available evidence.</p>
            )}
            {docs.map(d => (
              <label key={d.id} className="block">
                <input
                  type="checkbox"
                  disabled={!d.available}
                  checked={evidenceIds.includes(d.id)}
                  onChange={e => {
                    setAck(false)
                    setEvidenceIds(v =>
                      e.target.checked ? [...v, d.id] : v.filter(x => x !== d.id)
                    )
                  }}
                />
                {d.name} — version {d.version}
                {!d.available ? ' (unavailable)' : ''}
              </label>
            ))}
            <div>
              <button
                className="btn"
                disabled={docPage <= 1}
                onClick={() => void loadEvidence(docPage - 1)}
              >
                Previous evidence
              </button>
              <button
                className="btn"
                disabled={docPage * 20 >= docTotal}
                onClick={() => void loadEvidence(docPage + 1)}
              >
                Next evidence
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p>
              For an initial claim,you may declare sealed evidence references for independent
              review. This does not verify the evidence,create a Vault record or grant access. The
              reviewer must inspect the original bytes and custody under an approved policy.
            </p>
            <Field
              label="Sealed evidence reference"
              value={declaredId}
              onChange={e => {
                setDeclaredId(e.target.value)
                setAck(false)
              }}
            />
            <Field
              label="Declared evidence SHA-256"
              value={declaredHash}
              onChange={e => {
                setDeclaredHash(e.target.value)
                setAck(false)
              }}
            />
            <button
              className="btn"
              disabled={!source || declared.length >= 10}
              onClick={() => {
                try {
                  const reference = id(declaredId)
                  if (
                    !/^[a-f0-9]{64}$/.test(declaredHash) ||
                    declared.some(v => v.evidence_id === reference)
                  )
                    throw new Error()
                  setDeclared(v => [...v, { evidence_id: reference, digest: declaredHash }])
                  setDeclaredId('')
                  setDeclaredHash('')
                  setAck(false)
                  setError('')
                } catch {
                  setError(
                    'Use a distinct sealed evidence reference and its exact SHA-256 fingerprint.'
                  )
                }
              }}
            >
              Add unverified evidence declaration
            </button>
            {declared.map(d => (
              <div key={d.evidence_id}>
                <span>Unverified evidence reference: {d.evidence_id}</span>
                <button
                  className="btn"
                  onClick={() => {
                    setDeclared(v => v.filter(x => x.evidence_id !== d.evidence_id))
                    setAck(false)
                  }}
                >
                  Remove declaration {d.evidence_id}
                </button>
              </div>
            ))}
          </div>
        )}
        <label className="block">
          <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} />I declare
          this request and evidence for independent review;this does not establish legal control or
          grant access.
        </label>
        <button
          className="btn btn-primary"
          disabled={
            !source || !ack || !(evidenceMode === 'vault' ? evidenceIds.length : declared.length)
          }
          onClick={submit}
        >
          Submit unverified business request
        </button>
      </fieldset>
      {error && <p role="alert">{error}</p>}
      {receipt && (
        <p role="status">
          Business request: {String(receipt.state)}. No authority is granted by this receipt.
        </p>
      )}
      <fieldset disabled={busy || broken} className="space-y-3">
        <legend>Original business requests</legend>
        {refs.map(r => (
          <div key={r.request}>
            <p>
              Request: {r.request} — {r.unknown ? 'Outcome unconfirmed' : 'Recorded receipt'}
            </p>
            <button className="btn" onClick={() => recover(r)}>
              Check business request {r.request}
            </button>
          </div>
        ))}
        {active && (
          <div>
            <p>Selected original request: {active.request}</p>
            <button className="btn" disabled={!!active.cancel} onClick={retry}>
              Retry exact original business request
            </button>
            <label className="block">
              Business cancellation reason
              <select
                className="input"
                disabled={!!active.cancel}
                value={reason}
                onChange={e => {
                  setReason(e.target.value)
                  setCancelAck(false)
                }}
              >
                <option value="">Choose a reason</option>
                <option value="withdrawn">Withdrawn</option>
                <option value="incorrect_request">Incorrect request</option>
                <option value="evidence_correction">Evidence correction</option>
              </select>
            </label>
            <label>
              <input
                type="checkbox"
                checked={cancelAck}
                onChange={e => setCancelAck(e.target.checked)}
              />
              Cancel or fence this exact original unverified request.
            </label>
            <button
              className="btn"
              disabled={!cancelAck || (!reason && !active.cancel)}
              onClick={cancel}
            >
              {active.cancel
                ? 'Retry exact original business cancellation'
                : 'Cancel original business request'}
            </button>
            <button
              className="btn"
              disabled={refs.some(r => r.unknown)}
              onClick={() => {
                setActive(null)
                setReceipt(null)
                setSource(null)
                setAck(false)
                setCancelAck(false)
                setReason('')
                setAccount('')
                setCapacity('')
                setRole('')
                setActions([])
                setEvidenceIds([])
                setDeclared([])
                setDeclaredId('')
                setDeclaredHash('')
                setDocs([])
                setChoices([])
                setSelected('')
              }}
            >
              Begin another business request
            </button>
          </div>
        )}
      </fieldset>
    </section>
  )
}
